import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Activity, AlertCircle, CheckCircle2, Users, UserX, Building2, Link2,
  Eye, Trash2, RefreshCw, ArrowRight, Search, X, Wrench,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  OverallSummaryCard, MetricRow, CategoryGroup, StatusBadge, StatusDonut,
} from '@/components/scorecard/ScorecardPrimitives';
import CreativeMyScorecardView from '@/components/scorecard/CreativeMyScorecardView';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const h = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

function StatCard({ icon: Icon, label, value, sub, color = '#10B981', testId }) {
  return (
    <div data-testid={testId} className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-4 hover:border-[#10B981]/40 transition">
      <div className="flex items-center gap-2 text-[11px] text-[#9CA3AF] uppercase tracking-wider mb-1">
        <Icon size={12} style={{ color }} /> {label}
      </div>
      <div className="text-2xl font-semibold text-white">{value}</div>
      {sub && <div className="text-[10px] text-[#6B7280] mt-1">{sub}</div>}
    </div>
  );
}

function IssueSection({ title, count, items, renderItem, emptyMessage, severity = 'high' }) {
  const [collapsed, setCollapsed] = useState(count === 0);
  const colorMap = {
    high: { border: '#E63946', text: '#E63946', bg: 'bg-[#E63946]/5' },
    med: { border: '#F59E0B', text: '#F59E0B', bg: 'bg-[#F59E0B]/5' },
    low: { border: '#3B82F6', text: '#3B82F6', bg: 'bg-[#3B82F6]/5' },
  };
  const c = colorMap[severity];
  return (
    <div className={`bg-[hsl(var(--surface))] border rounded-xl overflow-hidden`} style={{ borderColor: count > 0 ? c.border + '55' : 'hsl(var(--border))' }}>
      <button
        onClick={() => setCollapsed(!collapsed)}
        data-testid={`health-section-${title.toLowerCase().replace(/\s+/g, '-')}`}
        className={`w-full flex items-center justify-between px-4 py-3 ${count > 0 ? c.bg : ''} hover:bg-[hsl(var(--surface-elevated))] transition`}
      >
        <div className="flex items-center gap-2.5">
          {count > 0 ? <AlertCircle size={15} style={{ color: c.text }} /> : <CheckCircle2 size={15} className="text-[#10B981]" />}
          <span className="text-sm font-medium text-white">{title}</span>
          <Badge className={count > 0 ? `text-[10px] bg-[${c.text}]/15 text-[${c.text}] border-0` : 'text-[10px] bg-[#10B981]/15 text-[#10B981] border-0'} style={count > 0 ? { backgroundColor: c.text + '22', color: c.text } : undefined}>
            {count}
          </Badge>
        </div>
        <ArrowRight size={13} className={`text-[#6B7280] transition-transform ${collapsed ? '' : 'rotate-90'}`} />
      </button>
      {!collapsed && (
        <div className="border-t border-[hsl(var(--border))] p-3 space-y-1.5 max-h-[400px] overflow-y-auto">
          {items.length === 0 ? (
            <p className="text-xs text-[#10B981] py-2 px-2">{emptyMessage}</p>
          ) : (
            items.map((it, i) => <div key={i}>{renderItem(it)}</div>)
          )}
        </div>
      )}
    </div>
  );
}

function PreviewAsUserModal({ open, user, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('');
  const [selectedChannel, setSelectedChannel] = useState('all');
  const [isCreativeMember, setIsCreativeMember] = useState(false);

  // Detect if previewed user is in creative team (so we show Creative View instead)
  useEffect(() => {
    if (!open || !user) return;
    setIsCreativeMember(false);
    axios.get(`${API}/creative-scorecard/team-members`, h())
      .then((r) => {
        const ids = r.data?.member_ids || [];
        if (user?.id && ids.includes(user.id)) {
          setIsCreativeMember(true);
        }
      })
      .catch(() => { /* non-fatal */ });
  }, [open, user]);

  useEffect(() => {
    if (!open || !user) return;
    // Skip standard preview fetch when previewing a creative member — CreativeMyScorecardView fetches its own.
    if (isCreativeMember) { setData(null); setLoading(false); return; }
    setSelectedChannel('all');
    const load = async () => {
      setLoading(true); setData(null);
      try {
        const url = period
          ? `${API}/scorecard/admin/preview-as/${user.id}?period=${period}`
          : `${API}/scorecard/admin/preview-as/${user.id}`;
        const r = await axios.get(url, h());
        setData(r.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load preview');
      } finally { setLoading(false); }
    };
    load();
  }, [open, user, period, isCreativeMember]);

  const breakdownToRender = selectedChannel === 'all'
    ? (data?.breakdown || [])
    : (data?.channels?.find(c => c.staff_node_id === selectedChannel)?.breakdown || []);
  const grouped = breakdownToRender.reduce((acc, t) => {
    const key = t.category || 'OTHER';
    (acc[key] = acc[key] || []).push(t);
    return acc;
  }, {});

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="preview-as-user-modal">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Eye size={16} className="text-[#3B82F6]" />
            Preview as {user?.name || user?.email}
            <Badge className="text-[9px] bg-[#3B82F6]/15 text-[#3B82F6] border-0 ml-2">READ-ONLY</Badge>
          </DialogTitle>
          <p className="text-[11px] text-[#6B7280] mt-1">Apa user ni nampak bila login dan buka &quot;My Scorecard&quot;. Audit log direkodkan.</p>
        </DialogHeader>

        <div className="flex items-center gap-2 py-2 border-b border-[hsl(var(--border))]">
          <label className="text-[11px] text-[#9CA3AF]">Period:</label>
          <Input value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="YYYY-MM (kosong = current)"
            data-testid="preview-period-input"
            className="bg-transparent border-[hsl(var(--border))] text-white text-xs h-7 w-40" />
          {data?.period && <Badge className="text-[10px] bg-[#10B981]/15 text-[#10B981] border-0">Now showing: {data.period}</Badge>}
        </div>

        {loading && <div className="py-12 text-center text-[#6B7280] text-sm">Loading scorecard...</div>}

        {/* CREATIVE TEAM MEMBER — show new Creative View instead of standard scorecard */}
        {isCreativeMember && (
          <div className="my-3" data-testid="preview-creative-view">
            <div className="bg-pink-500/10 border border-pink-500/20 rounded-md p-3 text-[12px] text-pink-300 mb-3 flex items-start gap-2">
              <Eye size={14} className="flex-shrink-0 mt-0.5" />
              <div>
                <strong className="text-pink-200">Creative Team Member</strong> — preview ini tunjuk apa yang user nampak dalam My Scorecard mereka: <strong>Team Creative</strong> (semua channel aggregate) + <strong>My Personal</strong> (individual breakdown). Standard scorecard view tak ditunjuk untuk creative members.
              </div>
            </div>
            <CreativeMyScorecardView period={period || new Date().toISOString().slice(0, 7)} targetUserId={user?.id} />
          </div>
        )}

        {!loading && !isCreativeMember && data && (
          <>
            {data.message && (
              <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-md p-3 text-[12px] text-[#F59E0B] my-3">
                ⚠️ {data.message}
              </div>
            )}
            {(data.staff_names || data.target_user) && (
              <div className="bg-[hsl(var(--surface-elevated))] rounded-md p-3 text-[11px] text-[#9CA3AF] my-2">
                <div><strong className="text-white">User:</strong> {data.target_user?.name} ({data.target_user?.email})</div>
                {data.staff_names && <div><strong className="text-white">Mapped to:</strong> {data.staff_names.join(', ')}</div>}
              </div>
            )}
            {data.overall && (
              <div className="my-3">
                <OverallSummaryCard overall={data.overall} label={`${data.target_user?.name || ''}'s Overall — ${data.period}`} />
              </div>
            )}
            {data.channels && data.channels.length > 0 && (
              <div className="my-3">
                <div className="flex items-center gap-2 mb-2">
                  <Building2 size={14} className="text-[#9CA3AF]" />
                  <h3 className="text-white text-sm font-medium">By Channel</h3>
                  <Badge className="text-[9px] bg-[hsl(var(--surface-elevated))] text-[#9CA3AF] border-0">{data.channels.length}</Badge>
                  <span className="text-[10px] text-[#6B7280] ml-auto italic">Klik kad untuk drill ke breakdown</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {data.channels.map((ch) => {
                    const accentMap = { on_track: '#10B981', at_risk: '#F59E0B', off_track: '#E63946', no_data: '#6B7280' };
                    const accent = accentMap[ch.overall?.status] || '#6B7280';
                    const avatarUrl = ch.brand_logo_url || ch.personal_image_url;
                    const initials = (ch.channel_name || ch.ib_name || '?')
                      .split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
                    const isActiveTab = selectedChannel === ch.staff_node_id;
                    return (
                      <div key={ch.staff_node_id || ch.ib_node_id}
                        onClick={() => setSelectedChannel(ch.staff_node_id)}
                        className={`group relative bg-[hsl(var(--surface-elevated))] rounded-lg p-3 border cursor-pointer transition-all overflow-hidden ${
                          isActiveTab ? 'border-[#10B981] shadow-[0_0_0_1px_rgba(16,185,129,0.2)]' : 'border-[hsl(var(--border))] hover:border-[hsl(var(--border-hover))]'
                        }`}
                        data-testid={`preview-channel-${ch.staff_node_id || ch.ib_node_id}`}>
                        <div className="absolute inset-x-0 top-0 h-0.5" style={{ background: `linear-gradient(90deg, ${accent}, ${accent}66, transparent)` }} />
                        <div className="flex items-start gap-2.5 mb-2">
                          <div className="relative w-9 h-9 rounded-lg overflow-hidden flex-shrink-0 ring-1 ring-[hsl(var(--border))]" style={{ background: `linear-gradient(135deg, ${accent}22, ${accent}11)` }}>
                            {avatarUrl ? (
                              <img src={avatarUrl} alt={ch.channel_name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-white font-bold text-[10px]" style={{ background: `linear-gradient(135deg, ${accent}, ${accent}80)` }}>
                                {initials}
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[9px] text-[#6B7280] uppercase">Channel</p>
                            <p className="text-white text-xs font-semibold truncate">{ch.channel_name || ch.ib_name || '—'}</p>
                            <p className="text-[10px] text-[#6B7280] truncate">{ch.staff_name}</p>
                          </div>
                          <StatusBadge status={ch.overall?.status || 'no_data'} />
                        </div>
                        <div className="flex items-end justify-between gap-2">
                          <p className="text-xl font-bold text-white tabular-nums">{Math.min(ch.overall?.overall_pct || 0, 100).toFixed(1)}<span className="text-xs text-[#6B7280]">%</span></p>
                          <StatusDonut overall={ch.overall} size={36} />
                        </div>
                        <div className="mt-2 pt-2 border-t border-[hsl(var(--border))] text-[10px] text-[#6B7280]">
                          {(ch.breakdown || []).length} metric{(ch.breakdown || []).length === 1 ? '' : 's'} · {ch.crm_connected ? 'CRM linked' : 'no CRM link'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {data.channels && data.channels.length > 1 && (
              <div className="my-3 flex items-center gap-1.5 flex-wrap bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-lg p-1">
                <button
                  onClick={() => setSelectedChannel('all')}
                  data-testid="preview-tab-all"
                  className={`px-3 py-1.5 rounded-md text-[11px] font-semibold transition flex items-center gap-1.5 ${
                    selectedChannel === 'all'
                      ? 'bg-gradient-to-r from-[#10B981] to-[#3B82F6] text-white'
                      : 'text-[#9CA3AF] hover:text-white hover:bg-[hsl(var(--surface))]'
                  }`}>
                  All ({data.channels.length})
                </button>
                {data.channels.map(ch => {
                  const isActive = selectedChannel === ch.staff_node_id;
                  const statusMap = { on_track: '#10B981', at_risk: '#F59E0B', off_track: '#E63946', no_data: '#6B7280' };
                  const dotColor = statusMap[ch.overall?.status] || '#6B7280';
                  return (
                    <button
                      key={ch.staff_node_id}
                      onClick={() => setSelectedChannel(ch.staff_node_id)}
                      data-testid={`preview-tab-${ch.staff_node_id}`}
                      className={`px-3 py-1.5 rounded-md text-[11px] font-semibold transition flex items-center gap-1.5 ${
                        isActive
                          ? 'bg-gradient-to-r from-[#10B981] to-[#3B82F6] text-white'
                          : 'text-[#9CA3AF] hover:text-white hover:bg-[hsl(var(--surface))]'
                      }`}>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: isActive ? '#fff' : dotColor }} />
                      {ch.channel_name || ch.ib_name}
                      <span className="text-[9px] opacity-70 tabular-nums">{Math.min(ch.overall?.overall_pct || 0, 100).toFixed(0)}%</span>
                    </button>
                  );
                })}
              </div>
            )}
            {Object.keys(grouped).length === 0 && !data.message && (
              <div className="py-8 text-center text-[#6B7280] text-sm">
                Tiada data untuk ditunjuk. Mungkin user belum di-link, takde target untuk period ni, atau takde achievement CRM.
              </div>
            )}
            {Object.entries(grouped).map(([cat, templates]) => (
              <CategoryGroup key={cat} categoryKey={cat} templates={templates} />
            ))}
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="preview-close-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminScorecardHealthCheck() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('');
  const [search, setSearch] = useState('');
  const [previewUser, setPreviewUser] = useState(null);
  const [creativeTeamIds, setCreativeTeamIds] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/scorecard/admin/health-check${period ? `?period=${period}` : ''}`, h());
      setData(r.data);
    } catch (e) {
      toast.error('Failed to load health check');
    } finally { setLoading(false); }
  };

  const loadCreativeTeam = async () => {
    try {
      const r = await axios.get(`${API}/creative-scorecard/team-members`, h());
      setCreativeTeamIds(r.data?.member_ids || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); }, [period]);
  useEffect(() => { loadCreativeTeam(); }, []);

  const cleanupOrphans = async () => {
    if (!window.confirm('Padam semua mapping yang point ke deleted node?')) return;
    try {
      const r = await axios.post(`${API}/scorecard/admin/health-check/cleanup-orphans`, {}, h());
      toast.success(`Cleaned ${r.data.cleaned} entries, removed ${r.data.removed} mappings`);
      load();
    } catch { toast.error('Cleanup failed'); }
  };

  if (loading && !data) return <div className="py-12 text-center text-[#6B7280] text-sm">Scanning scorecard health...</div>;
  if (!data) return null;

  const s = data.summary || {};
  const issues = data.issues || {};
  const summary = data.per_user_summary || [];
  const filteredSummary = search
    ? summary.filter(u => (u.name + ' ' + u.email).toLowerCase().includes(search.toLowerCase()))
    : summary;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-white text-base font-medium flex items-center gap-2">
            <Activity size={16} className="text-[#10B981]" /> Mapping Health Check
          </h3>
          <p className="text-[11px] text-[#6B7280] mt-1">Audit scorecard mapping, target coverage, dan preview apa setiap user nampak.</p>
        </div>
        <div className="flex items-center gap-2">
          <Input value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="YYYY-MM"
            data-testid="health-period-input"
            className="bg-transparent border-[hsl(var(--border))] text-white text-xs h-8 w-28" />
          <Button onClick={load} size="sm" variant="outline" data-testid="health-refresh-btn">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Users} label="Linked Users" value={`${s.linked_users}/${s.total_users}`} sub="Have scorecard mapping" color="#10B981" testId="health-stat-linked" />
        <StatCard icon={UserX} label="Unlinked" value={s.unlinked_users} sub="No mapping yet" color={s.unlinked_users > 0 ? '#E63946' : '#10B981'} testId="health-stat-unlinked" />
        <StatCard icon={Building2} label="Channels" value={`${s.total_ib_nodes - s.unlinked_ib_nodes}/${s.total_ib_nodes}`} sub="With CRM link" color="#F59E0B" testId="health-stat-channels" />
        <StatCard icon={Link2} label="Orphans" value={s.orphan_mappings} sub="Need cleanup" color={s.orphan_mappings > 0 ? '#F59E0B' : '#10B981'} testId="health-stat-orphans" />
      </div>

      {/* Issues sections */}
      <div className="space-y-2">
        <IssueSection
          title="Users without mapping"
          severity="high"
          count={issues.users_without_mapping?.length || 0}
          items={issues.users_without_mapping || []}
          emptyMessage="Semua users dah ada mapping ✓"
          renderItem={(u) => (
            <div className="flex items-center justify-between bg-[hsl(var(--surface-elevated))] rounded-md px-3 py-2">
              <div className="flex-1 min-w-0">
                <div className="text-xs text-white truncate">{u.name || u.email}</div>
                <div className="text-[10px] text-[#6B7280]">{u.email} · role: {u.role_id || '—'}</div>
              </div>
              <Badge className="text-[9px] bg-[#E63946]/15 text-[#E63946] border-0">no node</Badge>
            </div>
          )}
        />
        <IssueSection
          title="STAFF nodes without CRM user"
          severity="med"
          count={issues.nodes_without_link?.length || 0}
          items={issues.nodes_without_link || []}
          emptyMessage="Semua STAFF nodes dah link ✓"
          renderItem={(n) => (
            <div className="flex items-center justify-between bg-[hsl(var(--surface-elevated))] rounded-md px-3 py-2">
              <div className="text-xs text-white">{n.name}{n.parent_name && <span className="text-[#6B7280]"> · {n.parent_name}</span>}</div>
              <Badge className="text-[9px] bg-[#F59E0B]/15 text-[#F59E0B] border-0">no user</Badge>
            </div>
          )}
        />
        <IssueSection
          title="IB nodes (channels) without CRM link"
          severity="med"
          count={issues.ib_nodes_without_link?.length || 0}
          items={issues.ib_nodes_without_link || []}
          emptyMessage="Semua IB channels dah link ✓"
          renderItem={(n) => (
            <div className="flex items-center justify-between bg-[hsl(var(--surface-elevated))] rounded-md px-3 py-2">
              <div className="text-xs text-white">{n.name}</div>
              <Badge className="text-[9px] bg-[#F59E0B]/15 text-[#F59E0B] border-0">no channel</Badge>
            </div>
          )}
        />
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={async () => {
              if (!window.confirm('Auto-create IB org nodes untuk semua CRM channel AKTIF yang belum di-link?')) return;
              try {
                const r = await axios.post(`${API}/scorecard/admin/health-check/auto-link-channels`, {}, h());
                toast.success(`Created ${r.data.created_count}, linked ${r.data.linked_existing_count}, skipped ${r.data.skipped_count}`);
                load();
              } catch (e) { toast.error(e?.response?.data?.detail || 'Auto-link failed'); }
            }}
            size="sm" variant="outline" className="text-[#10B981] border-[#10B981]/40"
            data-testid="health-auto-link-channels-btn">
            <Wrench size={12} /> Auto-link Active CRM Channels
          </Button>
          <Button
            onClick={async () => {
              if (!window.confirm('Remove IB org nodes yang point ke channel INACTIVE/deleted? Cascade entries + mapping cleanup.')) return;
              try {
                const r = await axios.post(`${API}/scorecard/admin/health-check/auto-link-channels`, { remove_inactive_orgs: true }, h());
                toast.success(`Removed ${r.data.removed_inactive_count} inactive org nodes, created ${r.data.created_count} new`);
                load();
              } catch (e) { toast.error(e?.response?.data?.detail || 'Cleanup failed'); }
            }}
            size="sm" variant="outline" className="text-[#E63946] border-[#E63946]/40"
            data-testid="health-cleanup-inactive-orgs-btn">
            <Trash2 size={12} /> Remove inactive org nodes
          </Button>
          <span className="text-[10px] text-[#6B7280] italic w-full">Auto-link cipta node untuk channel <strong>aktif</strong> sahaja (cth: SBE). Remove inactive padam org nodes untuk channel yang dah di-deactivate.</span>
        </div>
        <IssueSection
          title="Channels without targets (current period)"
          severity="low"
          count={issues.channels_without_targets?.length || 0}
          items={issues.channels_without_targets || []}
          emptyMessage="Semua channel ada target ✓"
          renderItem={(n) => (
            <div className="flex items-center justify-between bg-[hsl(var(--surface-elevated))] rounded-md px-3 py-2">
              <div className="text-xs text-white">{n.name}</div>
              <Badge className="text-[9px] bg-[#3B82F6]/15 text-[#3B82F6] border-0">empty period</Badge>
            </div>
          )}
        />
        <IssueSection
          title="Orphan mappings"
          severity="med"
          count={issues.orphan_mappings?.length || 0}
          items={issues.orphan_mappings || []}
          emptyMessage="Takde orphan mappings ✓"
          renderItem={(o) => (
            <div className="flex items-center justify-between bg-[hsl(var(--surface-elevated))] rounded-md px-3 py-2">
              <div className="text-xs text-white">{o.crm_user_name}</div>
              <Badge className="text-[9px] bg-[#F59E0B]/15 text-[#F59E0B] border-0">{o.missing_node_ids.length} missing</Badge>
            </div>
          )}
        />
        <IssueSection
          title="Mapping drift (org-node vs mapping mismatch)"
          severity="high"
          count={issues.drift_users?.length || 0}
          items={issues.drift_users || []}
          emptyMessage="Mapping consistent across both surfaces ✓"
          renderItem={(d) => (
            <div className="flex items-start justify-between bg-[hsl(var(--surface-elevated))] rounded-md px-3 py-2 gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-xs text-white truncate">{d.crm_user_name}</div>
                <div className="text-[10px] text-[#6B7280]">
                  org nodes: {d.owned_node_count} · mapping: {d.mapped_node_count}
                  {d.owned_node_names?.length > 0 && <span className="ml-1">({d.owned_node_names.join(', ')})</span>}
                </div>
              </div>
              <Badge className="text-[9px] bg-[#E63946]/15 text-[#E63946] border-0">drift</Badge>
            </div>
          )}
        />
        {(issues.drift_users?.length || 0) > 0 && (
          <Button onClick={async () => {
            if (!window.confirm('Reconcile all drift? Mapping akan sync ikut org-node crm_user_id.')) return;
            try {
              const r = await axios.post(`${API}/scorecard/admin/health-check/reconcile-drift`, {}, h());
              toast.success(`Reconciled ${r.data.fixed} users, removed ${r.data.removed_orphan_users} orphan mappings`);
              load();
            } catch { toast.error('Reconcile failed'); }
          }} size="sm" variant="outline" className="text-[#E63946] border-[#E63946]/40" data-testid="health-reconcile-drift-btn">
            <Wrench size={12} /> Reconcile all drift
          </Button>
        )}
        {(issues.orphan_mappings?.length || 0) > 0 && (
          <Button onClick={cleanupOrphans} size="sm" variant="outline" className="text-[#F59E0B] border-[#F59E0B]/40" data-testid="health-cleanup-orphans-btn">
            <Trash2 size={12} /> Cleanup all orphan mappings
          </Button>
        )}
      </div>

      {/* Per-user summary */}
      <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[hsl(var(--border))]">
          <div className="flex items-center gap-2 text-sm text-white">
            <Users size={14} className="text-[#3B82F6]" /> Per-user Summary
            <Badge className="text-[10px] bg-[hsl(var(--surface-elevated))] text-[#9CA3AF] border-0">{filteredSummary.length}</Badge>
          </div>
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#6B7280]" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search user..."
              data-testid="health-user-search"
              className="bg-transparent border-[hsl(var(--border))] text-white text-xs h-7 pl-7 w-48" />
          </div>
        </div>
        <div className="max-h-[500px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-[hsl(var(--surface-elevated))] sticky top-0 z-10">
              <tr className="text-left text-[#6B7280] uppercase tracking-wider text-[10px]">
                <th className="px-4 py-2">User</th>
                <th className="px-2 py-2">Role</th>
                <th className="px-2 py-2">Nodes</th>
                <th className="px-2 py-2">Targets</th>
                <th className="px-2 py-2">Status</th>
                <th className="px-2 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredSummary.map((u) => {
                const isCreative = creativeTeamIds.includes(u.id);
                return (
                <tr key={u.id} className="border-t border-[hsl(var(--border))] hover:bg-[hsl(var(--surface-elevated))]/50 transition" data-testid={`health-user-row-${u.id}`}>
                  <td className="px-4 py-2">
                    <div className="text-white flex items-center gap-1.5">
                      {u.name || u.email}
                      {isCreative && <Badge className="text-[9px] bg-pink-500/15 text-pink-300 border border-pink-500/30">CREATIVE</Badge>}
                    </div>
                    <div className="text-[10px] text-[#6B7280]">{u.email}</div>
                  </td>
                  <td className="px-2 py-2">
                    <Badge className="text-[9px] bg-[hsl(var(--surface-elevated))] text-[#9CA3AF] border-0 capitalize">{u.role_id || '—'}</Badge>
                  </td>
                  <td className="px-2 py-2">
                    {u.node_count > 0 ? (
                      <div>
                        <span className="text-white">{u.node_count}</span>
                        <div className="text-[10px] text-[#6B7280] truncate max-w-[160px]" title={u.node_names.join(', ')}>{u.node_names.join(', ')}</div>
                      </div>
                    ) : <span className="text-[#6B7280]">0</span>}
                  </td>
                  <td className="px-2 py-2 text-white">{u.target_count}</td>
                  <td className="px-2 py-2">
                    {isCreative ? (
                      <Badge className="text-[9px] bg-pink-500/15 text-pink-300 border border-pink-500/30">creative team</Badge>
                    ) : u.has_mapping ? (
                      u.target_count > 0
                        ? <Badge className="text-[9px] bg-[#10B981]/15 text-[#10B981] border-0">ready</Badge>
                        : <Badge className="text-[9px] bg-[#F59E0B]/15 text-[#F59E0B] border-0">no targets</Badge>
                    ) : <Badge className="text-[9px] bg-[#E63946]/15 text-[#E63946] border-0">unlinked</Badge>}
                  </td>
                  <td className="px-2 py-2 text-right">
                    <Button onClick={() => setPreviewUser(u)} size="sm" variant="outline"
                      className="text-[#3B82F6] border-[#3B82F6]/40 text-[10px] h-6"
                      disabled={!u.has_mapping && !isCreative}
                      data-testid={`health-preview-btn-${u.id}`}>
                      <Eye size={10} /> Preview
                    </Button>
                  </td>
                </tr>
                );
              })}
              {filteredSummary.length === 0 && (
                <tr><td colSpan={6} className="text-center py-8 text-[#6B7280]">No users match search</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <PreviewAsUserModal
        open={!!previewUser}
        user={previewUser}
        onClose={() => setPreviewUser(null)}
      />
    </div>
  );
}
